import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useAuth } from '@/api/auth'
import styles from './LinkPreviews.module.css'

export interface LinkPreview {
  url: string
  title: string
  description: string | null
  hostname: string
  thumbnail_path: string | null
}

function destination(raw: string): string | null {
  try {
    const url = new URL(raw)
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null
  } catch { return null }
}

function Thumbnail({ path }: { path: string }) {
  const [source, setSource] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    const controller = new AbortController()
    let objectUrl: string | null = null
    setSource(null)
    setFailed(false)
    void api.image(path, controller.signal).then(blob => {
      if (controller.signal.aborted || !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(blob.type.split(';')[0] ?? '')) return
      objectUrl = URL.createObjectURL(blob)
      setSource(objectUrl)
    }).catch(() => { if (!controller.signal.aborted) setFailed(true) })
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [path])
  if (failed) return null
  return <span className={styles.thumbnail} aria-hidden="true">
    {source ? <img src={source} alt="" onError={() => setFailed(true)} /> : null}
  </span>
}

export function LinkPreviewCard({ preview, messageId }: { preview: LinkPreview; messageId: string }) {
  const href = destination(preview.url)
  if (!href) return null
  const prefix = `/messages/${encodeURIComponent(messageId)}/link-previews/`
  const thumbnail = preview.thumbnail_path?.startsWith(prefix) && /^[-\w]+\/image$/.test(preview.thumbnail_path.slice(prefix.length))
    ? preview.thumbnail_path : null
  return <a className={styles.card} href={href} target="_blank" rel="noopener noreferrer nofollow">
    {thumbnail ? <Thumbnail path={thumbnail} /> : null}
    <span className={styles.details}>
      <span className={styles.hostname}>{new URL(href).hostname}</span>
      <strong className={styles.title}>{preview.title}</strong>
      {preview.description ? <span className={styles.description}>{preview.description}</span> : null}
    </span>
  </a>
}

export function LinkPreviews({ messageId, text }: { messageId: string; text: string }) {
  const { user } = useAuth()
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const hasLink = /https?:\/\/|www\./i.test(text)
  useEffect(() => {
    if (!hasLink || visible) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect() }
    }, { rootMargin: '200px 0px' })
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [hasLink, visible])
  const query = useQuery({
    queryKey: ['link-previews', api.getBasePath(), user?.id, messageId, text],
    queryFn: ({ signal }) => api.get<{ previews: LinkPreview[] }>(`/messages/${encodeURIComponent(messageId)}/link-previews`, undefined, signal),
    enabled: hasLink && visible,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
  if (!hasLink) return null
  return <div ref={ref} className={styles.list}>
    {query.data?.previews.slice(0, 3).map(preview => <LinkPreviewCard key={preview.url} preview={preview} messageId={messageId} />)}
  </div>
}
